import React, { useState, useEffect } from 'react'

// Components
import { Box, Typography, Paper } from '@mui/material'
import {
  DataGrid,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid'
import { createAssetsColumnDefinitions } from '../../constants/columnDefinitions'
import DownloadIcon from '@mui/icons-material/Download'
import StyledButton from '../StyledButton/StyledButton'

// Store
import {
  useAssetsReportsStore,
  useAssetsReportsActions,
} from 'store/assetsReports/assetsReports.store'

// Types
import { AssetsTableProps } from './AssetsTable.types'

// Styles
import styles from './AssetsTable.module.scss'

const AssetsTable: React.FC<AssetsTableProps> = ({
  assets,
  isLoading,
  handleDownload,
}) => {
  const columns = createAssetsColumnDefinitions()
  const sortModel = useAssetsReportsStore((state) => state.filters.sortModel)
  const filterModel = useAssetsReportsStore(
    (state) => state.filters.filterModel
  )
  const columnVisibilityModel = useAssetsReportsStore(
    (state) => state.filters.columnVisibilityModel
  )
  const { setFilters } = useAssetsReportsActions()

  const [visibleRowCount, setVisibleRowCount] = useState(assets.length)

  const rows = assets.map((asset, index) => ({
    id: `${asset.serial}-${asset.date}-${index}`,
    ...asset,
  }))

  useEffect(() => {
    setVisibleRowCount(rows.length)
  }, [rows.length])

  useEffect(() => {
    if (filterModel?.items?.length === 0) {
      setVisibleRowCount(rows.length)
      return
    }

    let filteredCount = rows.length
    filterModel?.items?.forEach((filterItem) => {
      const { field, operator, value } = filterItem
      if (!value) return

      filteredCount = rows.filter((row) => {
        const cellValue = String((row as any)[field] || '').toLowerCase()
        const filterValue = String(value).toLowerCase()

        switch (operator) {
          case 'contains':
            return cellValue.includes(filterValue)
          case 'equals':
            return cellValue === filterValue
          case 'startsWith':
            return cellValue.startsWith(filterValue)
          case 'endsWith':
            return cellValue.endsWith(filterValue)
          default:
            return cellValue.includes(filterValue)
        }
      }).length
    })

    setVisibleRowCount(filteredCount)
  }, [filterModel, rows])

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setFilters({ sortModel: newSortModel })
  }

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilters({
      filterModel: newFilterModel,
    })
  }

  const handleColumnVisibilityModelChange = (
    newColumnVisibilityModel: GridColumnVisibilityModel
  ) => {
    setFilters({
      columnVisibilityModel: newColumnVisibilityModel,
    })
  }

  return (
    <Box className={styles.container}>
      <Paper elevation={2} className={styles.headerPaper}>
        <Box className={styles.headerContent}>
          <Box className={styles.headerInfo}>
            <Typography variant="h6" className={styles.title}>
              Products Report
              <Typography component="span" className={styles.countBadge}>
                {visibleRowCount}
              </Typography>
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              View and filter product usage data
            </Typography>
          </Box>
          <StyledButton
            onClick={handleDownload}
            className={styles.exportButton}
            label="Export Report"
            startIcon={<DownloadIcon />}
          />
        </Box>
      </Paper>

      <Paper elevation={2} className={styles.tablePaper}>
        <DataGrid
          rows={isLoading ? [] : rows}
          columns={columns}
          className={styles.dataGrid}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: sortModel,
            },
            filter: {
              filterModel: filterModel,
            },
            columns: {
              columnVisibilityModel: columnVisibilityModel,
            },
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          loading={isLoading}
        />
      </Paper>
    </Box>
  )
}

export default AssetsTable
